import { useEffect, useRef, useState, useMemo } from "react";
import "./Chat.css";
import CopyButton from "../textCopyButton/CopyButton";
import { parseMessage } from "../../utils/parseMessage";

const Chat = ({ user, socket }) => {
  const [messagesByRoom, setMessagesByRoom] = useState({});
  const [message, setMessage] = useState("");
  const [room, setRoom] = useState(null);
  const [roomUsers, setRoomUsers] = useState([]);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const hasAutoScrolled = useRef(false);

  // 🔹 messages for current room only
  const messages = useMemo(
    () => messagesByRoom[room] || [],
    [messagesByRoom, room],
  );

  // 🔹 Join room
  useEffect(() => {
    if (!socket || !room) return;
    socket.emit("joinPublicRoom", room);
  }, [socket, room]);

  // 🔹 Listen for live messages (ALL rooms, stored separately)
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (data) => {
      setMessagesByRoom((prev) => ({
        ...prev,
        [data.room]: [...(prev[data.room] || []), data],
      }));
    };

    socket.on("publicMessage", handleMessage);
    return () => socket.off("publicMessage", handleMessage);
  }, [socket]);

  // 🔹 Listen for history (PER room)
  useEffect(() => {
    if (!socket) return;

    const handleHistory = ({ room: historyRoom, messages }) => {
      setMessagesByRoom((prev) => ({
        ...prev,
        [historyRoom]: messages,
      }));
    };

    socket.on("publicHistory", handleHistory);
    return () => socket.off("publicHistory", handleHistory);
  }, [socket]);

  // total users in room
  useEffect(() => {
    if (!socket) return;

    const handleRoomUsers = (users) => {
      setRoomUsers(users);
    };

    socket.on("roomUsers", handleRoomUsers);

    return () => socket.off("roomUsers", handleRoomUsers);
  }, [socket]);

  // 🔹 Send message
  const sendMessage = () => {
    if (!message.trim() || !socket || !room) return;

    socket.emit("publicMessage", {
      room,
      text: message,
    });

    setMessage("");
  };

  // 🔹 Enter to send, Shift+Enter for newline
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // 🔹 Switch room (NORMALIZED)
  const switchRoom = (newRoom) => {
    const normalizedRoom = newRoom.trim().toLowerCase();
    setRoom(normalizedRoom); // frontend state
  };

  // 🔹 Auto scroll
  const isUserNearBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return true;

    const threshold = 120; // px
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  };

  useEffect(() => {
    if (!messages.length) return;

    // Force scroll on first load
    if (!hasAutoScrolled.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      hasAutoScrolled.current = true;
      return;
    }

    // Conditional scroll for new messages
    if (isUserNearBottom()) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    hasAutoScrolled.current = false;
  }, [room]);

  return (
    <div className="chat-box">
      <div className="chat-header">
        <h3>
          AIUB Public Chat - {room || "Select a Room"}
          <span className="room-users-count">online: {roomUsers.length}</span>
        </h3>
      </div>

      <div className="room-buttons">
        {["room 1", "room 2", "room 3"].map((r) => (
          <button
            key={r}
            onClick={() => switchRoom(r)}
            className={room === r.trim().toLowerCase() ? "active-room" : ""}
          >
            {r.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="chat-messages" ref={chatContainerRef}>
        {messages.map((msg, i) => {
          const isSelf = msg.user === user.username;
          const isSystem = msg.user === "System";

          return (
            <div
              key={i}
              className={`message ${
                isSystem ? "system" : isSelf ? "self" : "other"
              }`}
            >
              {!isSystem && !isSelf && (
                <div className="message-time">{msg.time}</div>
              )}

              <div className="message-content">
                {!isSystem && !isSelf && (
                  <div className="message-user-name">{msg.user}</div>
                )}
                <div className="message-text-body">
                  {parseMessage(msg.text).map((part) =>
                    part.type === "link" ? (
                      <a
                        key={part.key}
                        href={part.content}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1a73e8",
                          textDecoration: "underline",
                        }}
                      >
                        {part.content}
                      </a>
                    ) : (
                      <span key={part.key}>{part.content}</span>
                    ),
                  )}
                  {!isSystem && <CopyButton text={msg.text} />}
                </div>
              </div>

              {isSelf && !isSystem && (
                <div className="message-time">{msg.time}</div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          className="chat-input"
          rows="1"
        />
        <button onClick={sendMessage} className="chat-send-btn">
          Send
        </button>
      </div>
    </div>
  );
};

export default Chat;
